import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import Alert from '@/components/dialog/Alert'
import Confirm from '@/components/dialog/Confirm'

export interface AlertProps {
  type?: 'success' | 'warning' | 'error'
  message?: string | React.ReactNode
  title?: string
  btnName?: string
  onClose?: () => void
}

export interface ConfirmProps {
  message?: string | React.ReactNode
  title?: string
  confirmBtnName?: string
  cancelBtnName?: string
  onConfirm?: (inputValue?: string) => void
  onCancel?: () => void
  inputPlaceholder?: string
  inputRequired?: boolean
  inputValidator?: (value: string) => true | string
}

interface DialogContextType {
  showAlert: (params: AlertProps) => void
  showConfirm: (params: ConfirmProps) => void
}

const DialogContext = createContext<DialogContextType | undefined>(undefined)

export const useDialog = (): DialogContextType => {
  const context = useContext(DialogContext)
  if (!context) {
    throw new Error('현재 Dialog')
  }
  return context
}

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [alertData, setAlertData] = useState<AlertProps | null>(null)
  const [confirmData, setConfirmData] = useState<ConfirmProps | null>(null)

  const showAlert = (params: AlertProps) => {
    const { type, message, title, btnName, onClose } = params
    const resolvedTitle =
      title ||
      (type === 'success' ? 'Notice' : type === 'warning' ? 'Warning' : 'Error')

    setAlertData({ type, message, title: resolvedTitle, btnName, onClose })
  }

  const showConfirm = (params: ConfirmProps) => {
    console.log('showConfirm called with:', params) // 디버깅용
    const {
      title,
      message,
      confirmBtnName,
      cancelBtnName,
      onConfirm,
      onCancel,
      inputPlaceholder,
      inputRequired,
      inputValidator,
    } = params

    const resolvedTitle = title || 'Notice'

    const confirmData = {
      title: resolvedTitle,
      message,
      confirmBtnName,
      cancelBtnName,
      onConfirm,
      onCancel,
      inputPlaceholder,
      inputRequired,
      inputValidator,
    }

    console.log('Setting confirmData:', confirmData) // 디버깅용
    setConfirmData(confirmData)
  }

  const handleClose = () => {
    if (alertData?.onClose) {
      alertData.onClose()
    }
    setAlertData(null)
  }

  // Confirm 컴포넌트에서 직접 처리하도록 수정
  const handleConfirm = (inputValue?: string) => {
    if (confirmData?.onConfirm) {
      confirmData.onConfirm(inputValue)
    }
    setConfirmData(null)
  }

  const handleCancel = () => {
    if (confirmData?.onCancel) {
      confirmData.onCancel()
    }
    setConfirmData(null)
  }

  const handelKeyDown = (e: KeyboardEvent) => {
    if (!(alertData || confirmData)) return

    // 입력 필드에 포커스가 있을 때는 키보드 이벤트를 처리하지 않음
    if (document.activeElement instanceof HTMLInputElement) {
      console.log('Input field is focused, skipping keyboard handler')
      return
    }

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    if (e.code == 'Enter' || e.code == 'Space' || e.code == 'Spacebar') {
      if (alertData) {
        handleClose()
      } else if (confirmData) {
        // 입력이 필요한 경우 Enter 키로 확인하지 않도록 수정
        if (!(confirmData.inputPlaceholder || confirmData.inputRequired)) {
          handleConfirm()
        }
      }
    } else if (e.code == 'Escape') {
      if (alertData) {
        handleClose()
      } else if (confirmData) {
        handleCancel()
      }
    }
    e.preventDefault()
    e.stopPropagation()
    e.stopImmediatePropagation()
  }

  useEffect(() => {
    if (alertData || confirmData) {
      window.addEventListener('keydown', handelKeyDown)
    }
    return () => {
      window.removeEventListener('keydown', handelKeyDown)
    }
  }, [alertData, confirmData])

  return (
    <DialogContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      {alertData && (
        <Alert
          type={alertData.type || 'error'}
          message={alertData.message}
          title={alertData.title}
          btnName={alertData.btnName || '확인'}
          onClose={handleClose}
        />
      )}
      {confirmData && (
        <Confirm
          title={confirmData.title}
          message={confirmData.message}
          confirmBtnName={confirmData.confirmBtnName || '확인'}
          cancelBtnName={confirmData.cancelBtnName || '취소'}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          inputPlaceholder={confirmData.inputPlaceholder}
          inputRequired={confirmData.inputRequired}
          inputValidator={confirmData.inputValidator}
        />
      )}
    </DialogContext.Provider>
  )
}
